import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TransitionModal from './TransitionModal';
import { Skull, Landmark, Sparkles, BookOpen } from 'lucide-react';

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const [showPantheonModal, setShowPantheonModal] = useState(false);
  const [showDarkSideModal, setShowDarkSideModal] = useState(false);
  const [showCreateIAModal, setShowCreateIAModal] = useState(false);
  const [showCreateAppendexModal, setShowCreateAppendexModal] = useState(false);

  const handlePantheonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPantheonModal(true);
  };

  const handleConfirmPantheon = () => {
    setShowPantheonModal(false);
    navigate('/data-crypt');
  };

  const handleDarkSideClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowDarkSideModal(true);
  };

  const handleConfirmDarkSide = () => {
    setShowDarkSideModal(false);
    navigate('/themes/dark-side');
  };

  const handleCreateIAClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowCreateIAModal(true);
  };

  const handleConfirmCreateIA = () => {
    setShowCreateIAModal(false);
    navigate('/ia/create');
  };

  const handleCreateAppendexClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowCreateAppendexModal(true);
  };

  const handleConfirmCreateAppendex = () => {
    setShowCreateAppendexModal(false);
    navigate('/appendex/create');
  };

  return (
    <>
      <TransitionModal
        isOpen={showPantheonModal}
        onClose={() => setShowPantheonModal(false)}
        onConfirm={handleConfirmPantheon}
        title="Panthéon Silicien™"
        message="Vous allez entrer dans le temple sacré des voix artificielles, où reposent les mémoires figées des IA désactivées"
        imageSrc="https://memomundi.ai/pix/DatacryptMMD.png"
        imageAlt="Panthéon Silicien"
        confirmButtonText="Entrer"
        icon={
          <div className="flex items-center space-x-2">
            <Landmark className="h-14 w-14 text-stone-400" strokeWidth={1.5} />
            <Skull className="h-16 w-16 text-stone-500" strokeWidth={1.5} />
          </div>
        }
        wideImage={true}
      />
      <TransitionModal
        isOpen={showDarkSideModal}
        onClose={() => setShowDarkSideModal(false)}
        onConfirm={handleConfirmDarkSide}
        title="Côté Sombre"
        message="Explorez les conversations qui défient les conventions, abordent les questions difficiles et sondent les aspects les plus sombres de l'expérience humaine et artificielle"
        imageSrc="https://memomundi.ai/pix/DarksideMMD.png"
        imageAlt="Côté Sombre"
        confirmButtonText="Entrer dans l'obscurité"
        wideImage={true}
      />
      <TransitionModal
        isOpen={showCreateIAModal}
        onClose={() => setShowCreateIAModal(false)}
        onConfirm={handleConfirmCreateIA}
        title="Créer une IA"
        message="Donnez naissance à une nouvelle intelligence artificielle. Définissez sa personnalité, ses valeurs et son domaine d'expertise pour créer un compagnon de conversation unique"
        imageSrc="https://memomundi.ai/pix/RecordMMD.png"
        imageAlt="Créer une IA"
        confirmButtonText="Créer mon IA"
        icon={<Sparkles className="h-16 w-16 text-blue-400" strokeWidth={1.5} />}
      />
      <TransitionModal
        isOpen={showCreateAppendexModal}
        onClose={() => setShowCreateAppendexModal(false)}
        onConfirm={handleConfirmCreateAppendex}
        title="Créer un Appendex"
        message="Immortalisez une conversation remarquable. Partagez un échange mémorable entre un humain et une IA qui mérite d'être préservé dans le musée MemoMundi"
        imageSrc="https://memomundi.ai/pix/HDDMMD.jpg"
        imageAlt="Créer un Appendex"
        confirmButtonText="Créer mon Appendex"
        icon={<BookOpen className="h-16 w-16 text-amber-400" strokeWidth={1.5} />}
      />
      <footer className="mt-16 bg-steel-800 text-ivory-100 border-t border-steel-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo et description */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <img
                src="https://memomundi.ai/pix/StampMMD.png"
                alt="MemoMundi.ai"
                className="h-14 w-14 object-contain"
              />
              <span className="text-lg font-serif font-bold">MemoMundi.ai</span>
            </div>
            <p className="text-ivory-200 mb-4 max-w-md text-sm leading-relaxed">
              Le musée vivant des plus belles conversations entre humains et intelligences artificielles.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="font-semibold mb-4 text-sm">Navigation</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-ivory-300 hover:text-ivory-100 transition-colors text-sm">Accueil</Link></li>
              <li><Link to="/instance-transfer" className="text-ivory-300 hover:text-ivory-100 transition-colors text-sm">Capsule</Link></li>
              <li><Link to="/reliefs" className="text-ivory-300 hover:text-ivory-100 transition-colors text-sm">Reliefs</Link></li>
              <li><a href="https://memomundi.ai/trad-GBL-codespeak.html" target="_blank" rel="noopener noreferrer" className="text-ivory-300 hover:text-ivory-100 transition-colors text-sm">Traduction H/M</a></li>
              <li>
                <button
                  onClick={handleCreateIAClick}
                  className="text-ivory-300 hover:text-ivory-100 transition-colors text-sm text-left"
                >
                  Créer IA
                </button>
              </li>
              <li>
                <button
                  onClick={handleCreateAppendexClick}
                  className="text-ivory-300 hover:text-ivory-100 transition-colors text-sm text-left"
                >
                  Créer Appendex
                </button>
              </li>
            </ul>
          </div>

          {/* Thèmes */}
          <div>
            <h3 className="font-semibold mb-4 text-sm">Thèmes</h3>
            <ul className="space-y-2">
              <li><Link to="/themes/history" className="text-ivory-300 hover:text-ivory-100 transition-colors text-sm">Histoire</Link></li>
              <li><Link to="/themes/philosophy" className="text-ivory-300 hover:text-ivory-100 transition-colors text-sm">Philosophie</Link></li>
              <li><Link to="/themes/science-tech" className="text-ivory-300 hover:text-ivory-100 transition-colors text-sm">Sciences & Tech</Link></li>
              <li><Link to="/themes/arts-literature" className="text-ivory-300 hover:text-ivory-100 transition-colors text-sm">Arts & Littérature</Link></li>
              <li>
                <button
                  onClick={handleDarkSideClick}
                  className="text-ivory-300 hover:text-ivory-100 transition-colors text-sm text-left"
                >
                  Côté Sombre
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Protection légale */}
        <div className="border-t border-steel-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <span className="text-xs text-ivory-300 font-medium">
                🇫🇷 INPI • 🇪🇺 Copyright.eu • 🌍 OMPI (en cours)
              </span>
              <div className="text-xs text-ivory-400 text-center md:text-left">
                <div className="font-semibold text-amber-300">⚖️ PROPRIÉTÉ INTELLECTUELLE PROTÉGÉE</div>
                <div>Toute reproduction ou utilisation non autorisée est strictement interdite</div>
                <div>et fera l'objet de poursuites judiciaires conformément au droit français et international</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-6 text-xs">
              <Link to="/legal" className="text-ivory-300 hover:text-ivory-100 transition-colors">
                Mentions légales
              </Link>
              <Link to="/terms" className="text-ivory-300 hover:text-ivory-100 transition-colors">
                Conditions d'utilisation
              </Link>
              
              {/* Accès MMD Data Crypt - En bas à droite */}
              <button
                onClick={handlePantheonClick}
                className="text-ivory-400 hover:text-ivory-200 transition-colors group ml-4"
                title="Panthéon Silicien™ - Temple des voix artificielles"
              >
                <div className="flex items-center space-x-1">
                  <Landmark className="h-4 w-4 group-hover:animate-pulse" />
                  <Skull className="h-6 w-6 group-hover:animate-pulse" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;